import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Check, Images, ScanLine } from "lucide-react";

import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Estado } from "@/components/propuesta/piezas";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/compartidas")({
  component: Compartidas,
});

// ===========================================================================
// La misma foto en varias boletas.
//
// Contabilidad lo pidió como «verificar que las fotos no se repitan; la
// relación es 1 a 1». Al medirlo apareció el matiz que hace útil el aviso:
// **repetida no es duplicada**. Casi siempre es un cliente que paga dos lotes
// con una transferencia y adjunta el mismo voucher en las dos boletas.
//
// UN CASO ES UN GRUPO, NO UNA FOTO (2026-09-09)
// ---------------------------------------------
// Antes cada foto era una fila y se comparaba contra el total ENTERO de cada
// boleta donde estaba pegada. Como una boleta se paga con varios papeles, la
// cuenta salía corta siempre y los tres casos del periodo decían lo mismo:
// «cubre una parte». Ahora las boletas atadas por una foto compartida son un
// solo caso, con TODOS sus papeles, y la pregunta es la de contabilidad:
// ¿alcanza lo que respaldan los papeles para lo que reclaman las boletas?
// ===========================================================================

const TONO: Record<string, "bad" | "warn" | "neutro" | "ok"> = {
  respaldo_repetido: "bad",
  falta_respaldo: "warn",
  sobra_respaldo: "warn",
  sin_totales: "neutro",
  cuadra: "ok",
};

const NOMBRE: Record<string, string> = {
  respaldo_repetido: "el dinero está contado dos veces",
  falta_respaldo: "falta respaldo",
  sobra_respaldo: "sobra respaldo",
  sin_totales: "faltan importes",
  cuadra: "cuadra al céntimo",
};

type Boleta = {
  comprobanteId: string;
  serieNumero: string | null;
  cliente: string | null;
  total: number | null;
  cruzada: boolean;
};
type Voucher = {
  recorte: string;
  importe: number | null;
  enBoletas: number;
  sospechoso: boolean;
};
type Caso = {
  id: string;
  clase: string;
  dice: string;
  moneda: "PEN" | "USD" | null;
  cliente: string | null;
  deben: number;
  respaldan: number;
  diferencia: number;
  boletas: Boleta[];
  vouchers: Voucher[];
};

function Compartidas() {
  const { data, isPending } = api.compartidas.useQuery(["compartidas"]);
  const d = data?.status === 200 ? data.body : null;
  const casos = (d?.casos ?? []) as Caso[];
  const graves = casos.filter((c) => c.clase === "respaldo_repetido");
  const aReleer = casos.filter((c) => c.vouchers.some((v) => v.sospechoso));

  return (
    <>
      <Cabecera
        migas={["Conciliación"]}
        titulo="La misma foto en varias boletas"
      />

      <p className="-mt-2 max-w-3xl text-[13px] text-muted-foreground">
        Una foto pegada en dos expedientes casi siempre es correcta: un cliente
        paga dos lotes con una transferencia y el mismo voucher va en las dos
        boletas. Lo que hay que mirar es si los papeles alcanzan para lo que
        reclaman las boletas — cada papel cuenta <b>una sola vez</b>, aunque
        esté pegado en tres.
      </p>

      {graves.length > 0 ? (
        <Aviso tono="bad">
          <AlertTriangle className="mt-px size-4 shrink-0" />
          <span>
            <b>
              {graves.length} {graves.length === 1 ? "caso" : "casos"} con el
              dinero contado más de una vez
            </b>{" "}
            — el mismo voucher respalda varias boletas por su importe entero. Si
            se concilian así, el cierre cuadra contra dinero que entró una sola
            vez.
          </span>
        </Aviso>
      ) : casos.length > 0 ? (
        <Aviso tono="info">
          <Check className="mt-px size-4 shrink-0" />
          <span>
            <b>Ninguna foto está contando el dinero dos veces.</b> Quedan{" "}
            {casos.length} {casos.length === 1 ? "caso" : "casos"} por mirar.
          </span>
        </Aviso>
      ) : null}

      {aReleer.length > 0 ? (
        <Aviso tono="warn">
          <ScanLine className="mt-px size-4 shrink-0" />
          <span>
            <b>
              {aReleer.length}{" "}
              {aReleer.length === 1 ? "caso trae" : "casos traen"} un importe
              que no puede ser cierto
            </b>{" "}
            — un papel de mucho menos que la boleta que respalda no es un pago
            parcial: es una lectura mal hecha. Hay que releerlo antes de
            conciliar.
          </span>
        </Aviso>
      ) : null}

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="font-medium text-base">
            {/* «0 de 0 imágenes» es una frase sobre nada. Con el periodo vacío
                hay que decir que todavía no se leyó ninguna. */}
            {!d
              ? "Fotos compartidas"
              : d.imagenes === 0
                ? "Todavía no se ha leído ninguna imagen de voucher"
                : `${casos.length} ${casos.length === 1 ? "caso" : "casos"} · ${d.repetidas} de ${d.imagenes} fotos están en más de una boleta`}
          </CardTitle>
          {d && d.imagenes > 0 ? (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Check className="size-3.5" />
              {d.unaSola} cumplen el uno a uno
            </span>
          ) : null}
        </CardHeader>

        <CardContent className="space-y-3">
          {isPending ? (
            ["a", "b", "c"].map((k) => <Skeleton key={k} className="h-40" />)
          ) : !d || casos.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              {d && d.imagenes === 0
                ? "Esta pantalla se llena cuando se cargan los expedientes de los clientes y se leen sus vouchers."
                : "Ninguna foto de voucher está en más de una boleta."}
            </p>
          ) : (
            casos.map((c) => <CasoCompartido key={c.id} c={c} />)
          )}
        </CardContent>
      </Card>
    </>
  );
}

function CasoCompartido({ c }: { c: Caso }) {
  const m = c.moneda ?? "PEN";
  const falta = c.diferencia < 0;

  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
        <Estado tono={TONO[c.clase] ?? "neutro"}>
          {NOMBRE[c.clase] ?? c.clase}
        </Estado>
        <span className="font-medium">{c.cliente ?? "varios clientes"}</span>
        {/* La frase solo cuando dice algo que los tres números de abajo no
            dicen. Repetir «faltan 39.960» encima de «faltan 39.960» gasta el
            renglón que necesita el caso grave. */}
        {c.clase === "respaldo_repetido" || c.clase === "sin_totales" ? (
          <span className="text-[12px] text-muted-foreground">{c.dice}</span>
        ) : (
          <span className="text-[12px] text-muted-foreground">
            {c.boletas.length} boletas · {c.vouchers.length}{" "}
            {c.vouchers.length === 1 ? "papel" : "papeles"}
          </span>
        )}
      </div>

      {/* La resta, a la vista. Estaba calculada y no se enseñaba: sin los tres
          números no se puede comprobar nada, solo creer la frase. */}
      <div className="flex flex-wrap gap-x-8 gap-y-1 border-b bg-muted/30 px-4 py-2.5 text-[12.5px]">
        <span>
          las boletas reclaman{" "}
          <b className="tabular-nums">{dinero(c.deben, m)}</b>
        </span>
        <span>
          los papeles respaldan{" "}
          <b className="tabular-nums">{dinero(c.respaldan, m)}</b>
        </span>
        <span className={falta ? "text-amber-700 dark:text-amber-500" : ""}>
          {falta ? "faltan" : c.diferencia > 0 ? "sobran" : "diferencia"}{" "}
          <b className="tabular-nums">{dinero(Math.abs(c.diferencia), m)}</b>
        </span>
      </div>

      <table className="w-full text-[12.5px]">
        <tbody>
          {c.boletas.map((b) => (
            <tr key={b.comprobanteId} className="border-b">
              <td className="py-2 pl-4 text-muted-foreground">boleta</td>
              <td className="py-2 pl-3">
                <Link
                  to="/documentos/clientes"
                  className="font-medium hover:underline"
                  title="Ver el expediente de esta boleta"
                >
                  {b.serieNumero ?? "—"}
                </Link>
              </td>
              <td
                className="max-w-[16rem] truncate py-2 pl-3 text-muted-foreground"
                title={b.cliente ?? ""}
              >
                {b.cliente ?? "—"}
              </td>
              <td className="py-2 pl-3 text-right tabular-nums">
                {b.total !== null ? dinero(b.total, m) : "—"}
              </td>
              <td className="py-2 pr-4 pl-3 text-right">
                {b.cruzada ? (
                  <Estado tono="ok">cruzada</Estado>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    sin cruzar
                  </span>
                )}
              </td>
            </tr>
          ))}

          {c.vouchers.map((v) => (
            <tr key={v.recorte} className="border-b last:border-0">
              <td className="py-2 pl-4 text-muted-foreground">papel</td>
              <td className="py-2 pl-3">
                <a
                  href={`/imagen/${v.recorte}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 hover:underline"
                >
                  <Images className="size-3.5 shrink-0 text-muted-foreground" />
                  ver la foto
                </a>
              </td>
              <td className="py-2 pl-3 text-muted-foreground">
                {v.enBoletas > 1
                  ? `pegada en ${v.enBoletas} boletas`
                  : "en una sola"}
              </td>
              <td className="py-2 pl-3 text-right tabular-nums">
                {v.importe !== null ? dinero(v.importe, m) : "sin leer"}
              </td>
              <td className="py-2 pr-4 pl-3 text-right">
                {v.sospechoso ? (
                  <Estado tono="bad">releer</Estado>
                ) : (
                  <span className="text-[11px] text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function dinero(centimos: number, moneda: "PEN" | "USD"): string {
  const n = (centimos / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${moneda === "USD" ? "US$" : "S/"} ${n}`;
}
